import { useState, useCallback, useEffect, useMemo, Fragment } from 'react';
import { MagnifyingGlassIcon, FunnelIcon, PlusIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Input,
  Select,
  SearchableSelect,
  Card,
  Badge,
  getStatusBadgeVariant,
  Pagination,
  Modal,
  ModalFooter,
} from '@/components/common';
import { useTickets, useCreateTicket, useTicketCategories } from '@/hooks/useCommunications';
import { useCustomers } from '@/hooks/useCustomers';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { formatDateTime, titleCase } from '@/utils';
import type { TicketFilters } from '@/types';

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'waiting_customer', label: 'Waiting Customer' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const priorityOptions = [
  { value: '', label: 'All Priority' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const emptyDraft = { subject: '', description: '', support_category: '', customer_record: '', priority: 'medium' };

export function TicketsPage() {
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<TicketFilters>({});
  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `searchTerm` drives the Input synchronously, `debouncedTerm` (300ms)
  // feeds `filters.search`, replacing the old submit-on-Enter form.
  const { term: searchTerm, debouncedTerm, setTerm: setSearchTerm } = useDebouncedSearch();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTicket, setNewTicket] = useState(emptyDraft);

  const { data, isLoading } = useTickets(page, filters);
  const createMutation = useCreateTicket();

  // Board #234: the Support Category comes from the categories configured in
  // the system (a broker's own plus the platform-wide ones), not from a
  // free-text box that produced a different spelling every time.
  const { data: categories, isLoading: categoriesLoading, isError: categoriesFailed } = useTicketCategories();
  const categoryOptions = useMemo(
    () => (categories ?? []).map((c) => ({ value: c.id, label: c.name })),
    [categories],
  );

  // Board #234: the customer this ticket is about. The list is the broker-scoped
  // /customers/ endpoint, and the server re-checks the id on save -- a customer
  // from another broker is a 400 there, not merely absent from this dropdown.
  const { debouncedTerm: customerQuery, setTerm: setCustomerQuery } = useDebouncedSearch();
  const { data: customerPage, isLoading: customersLoading } = useCustomers(1, {
    search: customerQuery || undefined,
    page_size: 100,
  });
  const customerOptions = useMemo(
    () =>
      (customerPage?.results ?? []).map((c) => ({
        value: c.id,
        label: c.customer_code ? `${c.full_name} (${c.customer_code})` : c.full_name,
      })),
    [customerPage],
  );

  const tickets = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const itemsPerPage = 20;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  useEffect(() => {
    const next = debouncedTerm || undefined;
    setFilters((prev) => (prev.search === next ? prev : { ...prev, search: next }));
    setPage(1);
  }, [debouncedTerm]);

  const handleFilterChange = (key: keyof TicketFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
    }));
    setPage(1);
  };

  const canSubmit = Boolean(newTicket.subject && newTicket.description && newTicket.support_category);

  const handleCreate = () => {
    if (!canSubmit) return;
    createMutation.mutate(
      {
        subject: newTicket.subject,
        description: newTicket.description,
        priority: newTicket.priority,
        support_category: newTicket.support_category,
        // Optional: a ticket may legitimately not be about a specific customer.
        ...(newTicket.customer_record ? { customer_record: newTicket.customer_record } : {}),
      },
      {
        onSuccess: () => {
          setShowCreateModal(false);
          setNewTicket(emptyDraft);
          setCustomerQuery('');
          // Show the new ticket at the top of the list.
          setPage(1);
        },
      },
    );
  };

  const [expandedTicket, setExpandedTicket] = useState<string | null>(null);
  const toggleExpand = useCallback((id: string) => {
    setExpandedTicket((prev) => (prev === id ? null : id));
  }, []);

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <div>
      <PageHeader
        title="Support Tickets"
        subtitle={`${totalItems} tickets`}
        breadcrumbs={[
          { label: 'Communications', href: '/communications' },
          { label: 'Tickets' },
        ]}
        actions={
          <Button
            leftIcon={<PlusIcon className="w-5 h-5" />}
            onClick={() => setShowCreateModal(true)}
          >
            New Ticket
          </Button>
        }
      />

      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search tickets..."
              leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
            />
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            leftIcon={<FunnelIcon className="w-5 h-5" />}
          >
            Filters
          </Button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
            <Select
              options={statusOptions}
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              placeholder="Status"
            />
            <Select
              options={priorityOptions}
              value={filters.priority || ''}
              onChange={(e) => handleFilterChange('priority', e.target.value)}
              placeholder="Priority"
            />
            {/* No `placeholder` here on purpose: Select renders a placeholder
                as a second disabled option with the same empty value, which
                would sit in front of "All Categories" and hide it. */}
            <Select
              id="ticket-filter-category"
              aria-label="Support Category"
              options={[{ value: '', label: 'All Categories' }, ...categoryOptions]}
              value={filters.support_category || ''}
              onChange={(e) => handleFilterChange('support_category', e.target.value)}
              disabled={categoriesLoading || categoryOptions.length === 0}
            />
          </div>
        )}
      </Card>

      <Card padding="none">
        {isLoading ? (
          <TableSkeleton />
        ) : tickets.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Ticket
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Support Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {tickets.map((ticket) => (
                    <Fragment key={ticket.id}>
                    <tr
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                      onClick={() => toggleExpand(ticket.id)}
                    >
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{ticket.ticket_number}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">{ticket.subject}</p>
                        </div>
                      </td>
                      {/* Board #234: the mapped customer. Tickets raised before the
                          mapping existed have none -- say so and show the login
                          that raised it rather than printing a name we did not
                          actually map. */}
                      <td className="px-6 py-4 text-sm">
                        {ticket.customer_record_name ? (
                          <span className="text-gray-900 dark:text-gray-100">{ticket.customer_record_name}</span>
                        ) : (
                          <span
                            className="text-gray-400"
                            title="Not mapped to a customer — showing the account that raised the ticket"
                          >
                            {ticket.customer_name || '—'}
                            <span className="ml-1 text-xs">(unmapped)</span>
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {ticket.category_name || '—'}
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant={getPriorityBadgeVariant(ticket.priority)} size="sm">
                          {titleCase(ticket.priority)}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant={getStatusBadgeVariant(ticket.status)} size="sm">
                          {titleCase(ticket.status)}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {formatDateTime(ticket.created_at)}
                      </td>
                    </tr>
                    {expandedTicket === ticket.id && (
                      <tr className="bg-gray-50 dark:bg-gray-800/50">
                        <td colSpan={6} className="px-6 py-4">
                          <div className="text-sm text-gray-700 dark:text-gray-300">
                            <p className="font-medium mb-1">Description:</p>
                            <p className="whitespace-pre-wrap">{ticket.description || 'No description provided.'}</p>
                            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                              Support Category: {ticket.category_name || 'Not set'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Customer: {ticket.customer_record_name || 'Not mapped'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Raised by: {ticket.customer_name || 'Unknown'}</p>
                            {ticket.assigned_to_name && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">Assigned to: {ticket.assigned_to_name}</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      {/* Create Ticket Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Support Ticket"
      >
        <div className="space-y-4">
          <Input
            id="ticket-subject"
            label="Subject"
            value={newTicket.subject}
            onChange={(e) => setNewTicket((prev) => ({ ...prev, subject: e.target.value }))}
            placeholder="Brief description of the issue"
            required
          />

          {/* Board #234: sourced from the configured categories. If the list
              could not be loaded, say so -- an empty dropdown that looks
              selectable is how this platform has shipped dead pickers before. */}
          {categoriesFailed ? (
            <p className="text-sm text-red-600 dark:text-red-400">
              Support categories could not be loaded, so a ticket cannot be filed right now.
            </p>
          ) : !categoriesLoading && categoryOptions.length === 0 ? (
            <p className="text-sm text-amber-600 dark:text-amber-400">
              No support categories are configured yet. Add one under Support Categories before raising a ticket.
            </p>
          ) : (
            <Select
              id="ticket-support-category"
              label="Support Category"
              options={categoryOptions}
              value={newTicket.support_category}
              onChange={(e) => setNewTicket((prev) => ({ ...prev, support_category: e.target.value }))}
              placeholder={categoriesLoading ? 'Loading categories...' : 'Select a support category'}
              disabled={categoriesLoading}
              required
            />
          )}

          <SearchableSelect
            id="ticket-customer"
            label="Customer"
            options={customerOptions}
            value={newTicket.customer_record}
            onChange={(value) => setNewTicket((prev) => ({ ...prev, customer_record: value }))}
            onQueryChange={setCustomerQuery}
            placeholder={customersLoading ? 'Loading customers...' : 'Search a customer (optional)'}
            helperText="Only customers of your broker can be selected."
          />

          <Select
            id="ticket-priority"
            label="Priority"
            options={priorityOptions.filter((p) => p.value)}
            value={newTicket.priority}
            onChange={(e) => setNewTicket((prev) => ({ ...prev, priority: e.target.value }))}
          />
          <div>
            <label htmlFor="ticket-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <textarea
              id="ticket-description"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              rows={4}
              value={newTicket.description}
              onChange={(e) => setNewTicket((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Detailed description of the issue..."
            />
          </div>
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            isLoading={createMutation.isPending}
            disabled={!canSubmit}
          >
            Create Ticket
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-12 bg-gray-100 dark:bg-gray-700" />
      {[...Array(5)].map((_, i) => (
        <div key={i} className="h-16 border-t border-gray-100 dark:border-gray-700 flex items-center px-6 gap-4">
          <div className="flex-1">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2" />
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-48" />
          </div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20" />
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-full w-16" />
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-full w-20" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-28" />
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-12">
      <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">No tickets found</h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">No support tickets match your search criteria.</p>
    </div>
  );
}
